import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import logo from "../assets/aerw-logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Meet Your Companions', path: '/companions' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Contact', path: '/contact' },
  { name: 'Policies', path: '/policies' }, // ✅ ADD THIS LINE
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E2E8F0]' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={logo} alt="AERW Lifestyle" className="h-16 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#1E4F8A] bg-[#F7F9FC]'
                    : 'text-[#4A5568] hover:text-[#1E4F8A] hover:bg-[#F7F9FC]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button & Phone */}
          <div className="hidden lg:flex items-center space-x-4">
  <a 
    href="tel:07803174495" 
    className="flex items-center text-sm text-[#4A5568] hover:text-[#1E4F8A] transition-colors"
    data-testid="header-phone"
  >
    <Phone className="w-4 h-4 mr-2" />
    07803 174495
  </a>

  <Link
    to="/contact#consultation-form"
    data-testid="header-cta"
    className="bg-[#1E4F8A] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#163A66] transition-colors"
  >
    Book Consultation
  </Link>
</div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#F7F9FC] transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1A1A1A]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1A1A1A]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-[#E2E8F0]"
            data-testid="mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#1E4F8A] bg-[#F7F9FC]'
                      : 'text-[#4A5568] hover:text-[#1E4F8A] hover:bg-[#F7F9FC]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-[#E2E8F0] mt-4 space-y-3">
                <a 
                  href="tel:07803174495" 
                  className="flex items-center px-4 py-2 text-[#4A5568]"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  07803 174495
                </a>
                <Link
                  to="/contact"
                  className="block text-center bg-[#1E4F8A] text-white px-6 py-3 rounded-full font-medium"
                >
                  Book Consultation
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
